import React from 'react';
import { Card, CardContent } from '../ui/Card';
import { formatCurrency } from '../../lib/utils';
import { FlameIcon, ClockIcon, CalendarIcon, TrendingUpIcon, ZapIcon, UsersIcon } from 'lucide-react';
import type { PeakDay, PeakHour, ChannelBreakdown } from '../../lib/metrics-hooks';
interface MetricsPeakInsightsProps {
  peakDays: PeakDay[];
  peakHours: PeakHour[];
  channelBreakdown: ChannelBreakdown;
  totalOrders: number;
  totalRevenue: number;
  averageTicket: number;
}
export function MetricsPeakInsights({
  peakDays,
  peakHours,
  channelBreakdown,
  totalOrders,
  totalRevenue,
  averageTicket
}: MetricsPeakInsightsProps) {
  const totalChannel = channelBreakdown.qr + channelBreakdown.waiter + channelBreakdown.other;
  const qrPct = totalChannel > 0 ? Math.round(channelBreakdown.qr / totalChannel * 100) : 0;
  const waiterPct = totalChannel > 0 ? Math.round(channelBreakdown.waiter / totalChannel * 100) : 0;
  return <div className="space-y-4" data-id="element-289">
      {/* Summary Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-290">
        <Card data-id="element-291">
          <CardContent className="p-5" data-id="element-292">
            <div className="flex items-start gap-3" data-id="element-293">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-294">
                <TrendingUpIcon className="w-5 h-5 text-emerald-600" data-id="element-295" />
              </div>
              <div className="min-w-0" data-id="element-296">
                <p className="text-sm text-gray-500 font-medium" data-id="element-297">
                  Ingresos Totales
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-298">
                  {formatCurrency(totalRevenue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-299">
          <CardContent className="p-5" data-id="element-300">
            <div className="flex items-start gap-3" data-id="element-301">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-302">
                <UsersIcon className="w-5 h-5 text-blue-600" data-id="element-303" />
              </div>
              <div className="min-w-0" data-id="element-304">
                <p className="text-sm text-gray-500 font-medium" data-id="element-305">
                  Total Órdenes
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5" data-id="element-306">
                  {totalOrders}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-307">
          <CardContent className="p-5" data-id="element-308">
            <div className="flex items-start gap-3" data-id="element-309">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-310">
                <ZapIcon className="w-5 h-5 text-purple-600" data-id="element-311" />
              </div>
              <div className="min-w-0" data-id="element-312">
                <p className="text-sm text-gray-500 font-medium" data-id="element-313">
                  Ticket Promedio
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-314">
                  {formatCurrency(averageTicket)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Peak Insights Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-315">
        {/* Peak Day */}
        <Card data-id="element-316">
          <CardContent className="p-5" data-id="element-317">
            <div className="flex items-center gap-2 mb-3" data-id="element-318">
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center" data-id="element-319">
                <FlameIcon className="w-4 h-4 text-red-500" data-id="element-320" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-321">
                Día Pico
              </span>
            </div>
            {peakDays.length > 0 ? <div className="space-y-2" data-id="element-322">
                {peakDays.map((day, i) => <div key={day.date} className="flex items-center justify-between" data-id="element-323">
                    <div className="flex items-center gap-2" data-id="element-324">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-325">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-326">{day.label}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-327">
                      {day.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-328">Sin datos</p>}
          </CardContent>
        </Card>

        {/* Peak Hour */}
        <Card data-id="element-329">
          <CardContent className="p-5" data-id="element-330">
            <div className="flex items-center gap-2 mb-3" data-id="element-331">
              <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-332">
                <ClockIcon className="w-4 h-4 text-amber-600" data-id="element-333" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-334">
                Hora Pico
              </span>
            </div>
            {peakHours.length > 0 ? <div className="space-y-2" data-id="element-335">
                {peakHours.map((hour, i) => <div key={hour.hour} className="flex items-center justify-between" data-id="element-336">
                    <div className="flex items-center gap-2" data-id="element-337">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-338">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-339">
                        {hour.label}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-340">
                      {hour.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-341">Sin datos</p>}
          </CardContent>
        </Card>

        {/* QR Self-service */}
        <Card data-id="element-342">
          <CardContent className="p-5" data-id="element-343">
            <div className="flex items-center gap-2 mb-3" data-id="element-344">
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-345">
                <CalendarIcon className="w-4 h-4 text-emerald-600" data-id="element-346" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-347">
                Autoservicio QR
              </span>
            </div>
            <div className="space-y-1" data-id="element-348">
              <p className="text-2xl font-bold text-emerald-600" data-id="element-349">
                {channelBreakdown.qr}
              </p>
              <p className="text-sm text-gray-500" data-id="element-350">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-351">{qrPct}%</span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-352">
                {formatCurrency(channelBreakdown.qrRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Waiter */}
        <Card data-id="element-353">
          <CardContent className="p-5" data-id="element-354">
            <div className="flex items-center gap-2 mb-3" data-id="element-355">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-356">
                <UsersIcon className="w-4 h-4 text-blue-600" data-id="element-357" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-358">
                Mesero
              </span>
            </div>
            <div className="space-y-1" data-id="element-359">
              <p className="text-2xl font-bold text-blue-600" data-id="element-360">
                {channelBreakdown.waiter}
              </p>
              <p className="text-sm text-gray-500" data-id="element-361">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-362">
                  {waiterPct}%
                </span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-363">
                {formatCurrency(channelBreakdown.waiterRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}